import {
  AI_PLAY_POLICY_VERSION,
  AI_BID_POLICY_VERSION,
  AI_DECLARATION_POLICY_VERSION,
  actionToId,
  buildBidEvidence,
  buildUnscoredDeclarationEvidence,
  buildUnscoredPlayEvidence,
  contractTargetForBid,
  deriveDecisionRngSeed,
  isInternalAIConfig,
  selectAIActionAnalysis,
  toAIStrategyConfig,
  SearchDeadlineExceeded,
  type FilteredGameState,
  type AIActionSelection,
  type SerializableAIConfig,
  type ValidAction,
} from '@texas42/game-runtime';
import type { DecisionDiagnostics } from './ai-worker-protocol.js';

export interface DecideAIActionInput {
  playerId: number;
  viewState: FilteredGameState;
  validActions: ValidAction[];
  config: SerializableAIConfig;
  deadlineMs: number;
  /**
   * When true, treat the deadline as a hard search cutoff (advice).
   * Product DECIDE keeps targetThinkMs observational so strength stays seed-stable.
   */
  enforceDeadline?: boolean;
  fallback?: boolean;
}

export interface DecideAIActionResult {
  transitionId: string;
  diagnostics: DecisionDiagnostics;
  nearEqualCandidateCount?: number;
}

export function deriveMatchAiRngSeed(shuffleSeed: number): number {
  return (shuffleSeed * 1_000_003) ^ 0x5bf0_3635;
}

function filterPlayerActions(validActions: ValidAction[], playerId: number): ValidAction[] {
  return validActions.filter((validAction) => {
    if (!('player' in validAction.action)) return true;
    return validAction.action.player === playerId;
  });
}

function estimateWorldsEvaluated(
  config: SerializableAIConfig,
  candidateCount: number,
  phase: FilteredGameState['phase'],
  simulationCount?: number,
): number | undefined {
  if (isInternalAIConfig(config)) {
    if (config.legacyStrategy !== 'beginner') return undefined;
    const biddingSimulations = config.monteCarloConfig?.biddingSimulations ?? 5;
    if (phase === 'bidding') return biddingSimulations * candidateCount;
    if (phase === 'playing') return simulationCount;
    return undefined;
  }

  const profile = config.profile;
  if (phase === 'bidding') return profile.biddingMinimumSamples * candidateCount;
  if (phase === 'playing') return simulationCount;
  return undefined;
}

function strategyLabel(config: SerializableAIConfig, fallback: boolean): string {
  if (fallback) return 'light-fallback';
  if (isInternalAIConfig(config)) return config.legacyStrategy;
  return config.profile.id;
}

/**
 * Light fallback when the worker is unavailable: first legal action for the seat.
 */
export function decideLightFallbackAction(
  playerId: number,
  validActions: ValidAction[],
  bids: FilteredGameState['bids'],
  currentBid?: FilteredGameState['currentBid'],
): DecideAIActionResult | null {
  const myActions = filterPlayerActions(validActions, playerId);
  const chosen = myActions[0];
  if (!chosen) return null;

  const playActions = myActions.filter((validAction) => validAction.action.type === 'play');
  const evidence =
    chosen.action.type === 'play' && playActions.length > 0
      ? buildUnscoredPlayEvidence(playActions, chosen, 'fallback')
      : chosen.action.type === 'bid' || chosen.action.type === 'pass'
        ? buildBidEvidence([], chosen, [], 'unscored', bids)
        : chosen.action.type === 'select-trump' && currentBid !== undefined
          ? buildUnscoredDeclarationEvidence(
              myActions,
              chosen,
              contractTargetForBid(currentBid),
            )
        : undefined;

  return {
    transitionId: actionToId(chosen.action),
    diagnostics: {
      elapsedMs: 0,
      strategy: 'light-fallback',
      fallback: true,
      candidateCount: myActions.length,
      ...(evidence ? { evidence } : {}),
    },
  };
}

function decisionReceiptContext(
  input: DecideAIActionInput,
): { policyVersion: number; decisionSeed: number } | undefined {
  if (
    input.viewState.phase !== 'playing' &&
    input.viewState.phase !== 'bidding' &&
    input.viewState.phase !== 'trump_selection'
  ) return undefined;
  return {
    policyVersion:
      input.viewState.phase === 'bidding'
        ? AI_BID_POLICY_VERSION
        : input.viewState.phase === 'trump_selection'
          ? AI_DECLARATION_POLICY_VERSION
          : AI_PLAY_POLICY_VERSION,
    decisionSeed: deriveDecisionRngSeed(input.config.aiRngSeed, input.viewState, input.playerId),
  };
}

export function decideAIAction(input: DecideAIActionInput): DecideAIActionResult | null {
  const myActions = filterPlayerActions(input.validActions, input.playerId);
  if (myActions.length === 0) return null;

  const startedAt = performance.now();
  const thinkTargetMs = isInternalAIConfig(input.config)
    ? input.deadlineMs
    : Math.min(input.deadlineMs, input.config.profile.targetThinkMs);
  const enforceDeadline = input.enforceDeadline === true;

  let selection: AIActionSelection | null = null;
  let deadlineExceeded = false;
  try {
    selection = selectAIActionAnalysis(
      input.viewState,
      input.playerId,
      input.validActions,
      input.config,
      enforceDeadline ? { deadlineAt: startedAt + thinkTargetMs } : undefined,
    );
  } catch (error) {
    if (!(error instanceof SearchDeadlineExceeded)) throw error;
    deadlineExceeded = true;
    selection = null;
  }
  const elapsedMs = performance.now() - startedAt;
  const chosen = selection?.action;
  const observedPastTarget = thinkTargetMs <= 0 || elapsedMs > thinkTargetMs;
  // DECIDE: targetThinkMs is telemetry only. ADVISE: only mark exceeded when search was cut off.
  const deadlineExceededFlag = enforceDeadline ? deadlineExceeded : deadlineExceeded || observedPastTarget;

  if (!selection || !chosen || selection.status === 'fallback') {
    const fallback = decideLightFallbackAction(
      input.playerId,
      input.validActions,
      input.viewState.bids,
      input.viewState.currentBid,
    );
    if (!fallback) return null;
    const evidence =
      selection?.evidence?.selectedActionId === fallback.transitionId
        ? selection.evidence
        : fallback.diagnostics.evidence;
    const receiptContext = decisionReceiptContext(input);
    return {
      ...fallback,
      diagnostics: {
        ...fallback.diagnostics,
        elapsedMs,
        deadlineExceeded: deadlineExceededFlag,
        ...(receiptContext ? receiptContext : {}),
        ...(evidence ? { evidence } : {}),
      },
    };
  }

  const diagnostics: DecisionDiagnostics = {
    elapsedMs,
    deadlineExceeded: deadlineExceededFlag,
    strategy: strategyLabel(input.config, input.fallback ?? false),
    candidateCount: myActions.length,
  };

  const scoredSimulationCount = selection.evidence?.candidates.find(
    (candidate) => 'simulationCount' in candidate && candidate.simulationCount !== undefined,
  );
  const simulationCount = scoredSimulationCount && 'simulationCount' in scoredSimulationCount
    ? scoredSimulationCount.simulationCount
    : undefined;
  const worldsEvaluated = estimateWorldsEvaluated(
    input.config,
    myActions.length,
    input.viewState.phase,
    simulationCount,
  );
  if (worldsEvaluated !== undefined) {
    diagnostics.worldsEvaluated = worldsEvaluated;
  }
  if (input.fallback) {
    diagnostics.fallback = true;
  }

  const receiptContext = decisionReceiptContext(input);
  if (receiptContext) {
    diagnostics.policyVersion = receiptContext.policyVersion;
    diagnostics.decisionSeed = receiptContext.decisionSeed;
  }

  if (!isInternalAIConfig(input.config)) {
    diagnostics.profileId = input.config.profile.id;
    const resolved = toAIStrategyConfig(input.config);
    if (resolved.profileId) {
      diagnostics.profileId = resolved.profileId;
    }
  }

  const evidence = selection.evidence;
  if (evidence) {
    diagnostics.evidence = evidence;
  }

  return {
    transitionId: actionToId(chosen.action),
    diagnostics,
    ...(selection.nearEqualCandidateCount !== undefined
      ? { nearEqualCandidateCount: selection.nearEqualCandidateCount }
      : {}),
  };
}
